import React, { useState, useMemo } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const TextDiffCheckerPage = () => {
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [diffMode, setDiffMode] = useState('words'); // 'words', 'lines', 'characters'
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);

  // Simple diff algorithm
  const calculateDiff = useMemo(() => {
    if (!text1 && !text2) return { diff1: [], diff2: [], stats: { added: 0, removed: 0, unchanged: 0 } };

    let processedText1 = text1;
    let processedText2 = text2;

    // Apply preprocessing options
    if (ignoreCase) {
      processedText1 = processedText1.toLowerCase();
      processedText2 = processedText2.toLowerCase();
    }

    if (ignoreWhitespace) {
      processedText1 = processedText1.replace(/\s+/g, ' ').trim();
      processedText2 = processedText2.replace(/\s+/g, ' ').trim();
    }

    let items1, items2;
    
    // Split text based on diff mode
    switch (diffMode) {
      case 'lines':
        items1 = processedText1.split('\n');
        items2 = processedText2.split('\n');
        break;
      case 'words':
        items1 = processedText1.split(/(\s+)/);
        items2 = processedText2.split(/(\s+)/);
        break;
      case 'characters':
        items1 = processedText1.split('');
        items2 = processedText2.split('');
        break;
      default:
        items1 = processedText1.split(/(\s+)/);
        items2 = processedText2.split(/(\s+)/);
    }

    // Simple LCS-based diff algorithm
    const lcs = longestCommonSubsequence(items1, items2);
    const diff1 = generateDiff(items1, items2, lcs, 'left');
    const diff2 = generateDiff(items2, items1, lcs, 'right');

    // Calculate statistics
    const stats = {
      added: diff2.filter(item => item.type === 'added').length,
      removed: diff1.filter(item => item.type === 'removed').length,
      unchanged: diff1.filter(item => item.type === 'unchanged').length
    };

    return { diff1, diff2, stats };
  }, [text1, text2, diffMode, ignoreCase, ignoreWhitespace]);

  // Longest Common Subsequence algorithm
  function longestCommonSubsequence(arr1, arr2) {
    const m = arr1.length;
    const n = arr2.length;
    const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (arr1[i - 1] === arr2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    // Backtrack to find LCS
    const lcs = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
      if (arr1[i - 1] === arr2[j - 1]) {
        lcs.unshift({ item: arr1[i - 1], i1: i - 1, i2: j - 1 });
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }

    return lcs;
  }

  // Generate diff with types (added, removed, unchanged)
  function generateDiff(arr1, arr2, lcs, side) {
    const result = [];
    let i = 0;
    let lcsIndex = 0;

    while (i < arr1.length) {
      if (lcsIndex < lcs.length && i === (side === 'left' ? lcs[lcsIndex].i1 : lcs[lcsIndex].i2)) {
        result.push({ type: 'unchanged', content: arr1[i] });
        lcsIndex++;
      } else {
        result.push({ type: side === 'left' ? 'removed' : 'added', content: arr1[i] });
      }
      i++;
    }

    return result;
  }

  const renderDiff = (diff, side) => {
    return diff.map((item, index) => {
      let backgroundColor = 'transparent';
      let color = '#333';
      
      switch (item.type) {
        case 'added':
          backgroundColor = '#d4edda';
          color = '#155724';
          break;
        case 'removed':
          backgroundColor = '#f8d7da';
          color = '#721c24';
          break;
        case 'unchanged':
          backgroundColor = 'transparent';
          color = '#666';
          break;
      }

      return (
        <span
          key={index}
          style={{
            backgroundColor,
            color,
            padding: diffMode === 'characters' ? '1px' : '2px 4px',
            margin: diffMode === 'characters' ? '0' : '1px',
            borderRadius: '3px',
            display: diffMode === 'lines' ? 'block' : 'inline',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            overflowWrap: 'anywhere'
          }}
        >
          {item.content}
        </span>
      );
    });
  };

  const loadSampleTexts = () => {
    setText1(`The quick brown fox jumps over the lazy dog.
This is the first version of the text.
It contains some sample content for testing.
Line four has some content.
Final line of the original text.`);

    setText2(`The quick brown fox leaps over the lazy cat.
This is the second version of the text.
It contains some sample content for testing purposes.
Line four has different content.
Final line of the modified text.
An additional line was added here.`);
  };

  const clearTexts = () => {
    setText1('');
    setText2('');
  };

  const swapTexts = () => {
    const temp = text1;
    setText1(text2);
    setText2(temp);
  };

  return (
    <div>
      <SEO 
        title="Text Diff Checker - Compare Text Differences Online" 
        description="Free online text diff checker. Compare two texts and highlight differences with word-by-word, line-by-line, or character-by-character comparison."
        keywords="text diff, text compare, diff checker, text comparison, file diff, text differences, compare documents"
      />
      
      <div className="container" style={{ maxWidth: '90%', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Text Diff Checker</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Compare two texts and highlight differences with customizable comparison modes.
        </p>
        
        <AdUnit />

        {/* Controls */}
        <div className="controls-section" style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          alignItems: 'center', 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
              Comparison Mode:
            </label>
            <select
              value={diffMode}
              onChange={(e) => setDiffMode(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="words">Word by Word</option>
              <option value="lines">Line by Line</option>
              <option value="characters">Character by Character</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={ignoreCase}
                onChange={(e) => setIgnoreCase(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Ignore Case
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Ignore Whitespace
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            <button
              onClick={loadSampleTexts}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Load Sample
            </button>
            <button
              onClick={swapTexts}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ↔ Swap
            </button>
            <button
              onClick={clearTexts}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Statistics */}
        {(text1 || text2) && (
          <div className="stats-section" style={{ 
            display: 'flex', 
            gap: '20px', 
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#e9ecef',
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {calculateDiff.stats.unchanged}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Unchanged</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                {calculateDiff.stats.removed}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Removed</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {calculateDiff.stats.added}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Added</div>
            </div>
            <div style={{ textAlign: 'center', marginLeft: 'auto' }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Mode: <strong>{diffMode}</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {ignoreCase && 'Case ignored • '}
                {ignoreWhitespace && 'Whitespace ignored'}
              </div>
            </div>
          </div>
        )}

        {/* Input and Diff Display */}
        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Original Text */}
          <div className="text-section">
            <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#dc3545' }}>
              Original Text
              {text1 && (
                <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
                  ({text1.length} characters)
                </span>
              )}
            </h2>
            
            <textarea
              value={text1}
              onChange={(e) => setText1(e.target.value)}
              placeholder="Enter the original text here..."
              style={{
                width: '100%',
                height: '200px',
                padding: '12px',
                border: '2px solid #dc3545',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box',
                marginBottom: '15px'
              }}
            />

            {/* Diff Display for Text 1 */}
            {(text1 || text2) && (
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '8px' }}>With Differences:</h3>
                <div style={{
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  minHeight: '150px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%'
                }}>
                  {renderDiff(calculateDiff.diff1, 'left')}
                </div>
              </div>
            )}
          </div>

          {/* Modified Text */}
          <div className="text-section">
            <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#28a745' }}>
              Modified Text
              {text2 && (
                <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
                  ({text2.length} characters)
                </span>
              )}
            </h2>
            
            <textarea
              value={text2}
              onChange={(e) => setText2(e.target.value)}
              placeholder="Enter the modified text here..."
              style={{
                width: '100%',
                height: '200px',
                padding: '12px',
                border: '2px solid #28a745',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box',
                marginBottom: '15px'
              }}
            />

            {/* Diff Display for Text 2 */}
            {(text1 || text2) && (
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '8px' }}>With Differences:</h3>
                <div style={{
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  minHeight: '150px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%'
                }}>
                  {renderDiff(calculateDiff.diff2, 'right')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="legend-section" style={{ 
          marginTop: '30px', 
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>Legend:</h3>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                backgroundColor: '#f8d7da', 
                color: '#721c24',
                padding: '4px 8px', 
                borderRadius: '3px',
                fontSize: '12px'
              }}>
                Removed
              </span>
              <span style={{ fontSize: '14px', color: '#666' }}>Text that was deleted</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                backgroundColor: '#d4edda', 
                color: '#155724',
                padding: '4px 8px', 
                borderRadius: '3px',
                fontSize: '12px'
              }}>
                Added
              </span>
              <span style={{ fontSize: '14px', color: '#666' }}>Text that was added</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                backgroundColor: 'transparent', 
                color: '#666',
                padding: '4px 8px', 
                borderRadius: '3px',
                fontSize: '12px',
                border: '1px solid #ddd'
              }}>
                Unchanged
              </span>
              <span style={{ fontSize: '14px', color: '#666' }}>Text that remained the same</span>
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>How to Use Text Diff Checker</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Comparison Modes</h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', paddingLeft: '20px' }}>
                <li><strong>Word by Word:</strong> Compares text word by word, ideal for prose and documents</li>
                <li><strong>Line by Line:</strong> Compares text line by line, perfect for code and structured text</li>
                <li><strong>Character by Character:</strong> Compares every character, useful for precise differences</li>
              </ul>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Options</h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', paddingLeft: '20px' }}>
                <li><strong>Ignore Case:</strong> Treats uppercase and lowercase letters as the same</li>
                <li><strong>Ignore Whitespace:</strong> Normalizes spaces, tabs, and line breaks</li>
                <li><strong>Load Sample:</strong> Loads example texts to see how the tool works</li>
                <li><strong>Swap:</strong> Exchanges the original and modified texts</li>
              </ul>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Use Cases</h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', paddingLeft: '20px' }}>
                <li>Compare document versions</li>
                <li>Review code changes</li>
                <li>Check text modifications</li>
                <li>Validate content updates</li>
                <li>Analyze text differences</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 15px;
          }
          
          .controls-section {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .controls-section > div:last-child {
            margin-left: 0 !important;
            margin-top: 15px;
          }
          
          .stats-section {
            flex-direction: column !important;
            text-align: center;
          }
          
          .stats-section > div:last-child {
            margin-left: 0 !important;
            margin-top: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default TextDiffCheckerPage; 